import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';
import { queryKeys } from './queryClient';
import type {
  ContractListItem,
  ContractDetail,
  CreateContract,
  UpdateContract,
  PagedResult,
  ContractStatus,
} from '../types';

/**
 * Contract list query parameters
 *
 * Supports filtering, sorting, and pagination for contract queries
 */
export interface ContractQueryParams {
  /** Search term for filtering by title, contract number, or vendor */
  search?: string;
  /** Filter by contract status */
  status?: ContractStatus;
  /** Filter by vendor ID */
  vendorId?: string;
  /** Filter by department ID */
  departmentId?: string;
  /** Leave out this contract and its descendants (parent pickers) */
  excludeSubtreeOf?: string;
  /** Include archived contracts (the API hides them by default) */
  includeArchived?: boolean;
  /** Filter by contract type */
  contractType?: string;
  /** Sort field name */
  sortBy?: string;
  /** Sort direction */
  sortOrder?: 'asc' | 'desc';
  /** Page number (1-indexed) */
  pageNumber?: number;
  /** Number of items per page */
  pageSize?: number;
}

/**
 * Fetch paginated list of contracts
 *
 * Retrieves contracts with optional filtering, sorting, and pagination.
 *
 * @param params - Query parameters for filtering and pagination
 * @returns Promise resolving to paged contract results
 */
const fetchContracts = async (
  params?: ContractQueryParams
): Promise<PagedResult<ContractListItem>> => {
  // The API's PaginationParams binds `page` and the boolean `sortDesc`, not
  // `pageNumber` and `sortOrder`; translate so paging and descending sorts
  // actually reach the server.
  const { pageNumber, sortOrder, ...rest } = params ?? {};
  return api.get<PagedResult<ContractListItem>>('/contracts', {
    params: {
      ...rest,
      page: pageNumber,
      sortDesc: sortOrder === undefined ? undefined : sortOrder === 'desc',
    },
  });
};

/**
 * Fetch single contract by ID
 *
 * Retrieves detailed information about a specific contract.
 *
 * @param id - Contract identifier
 * @returns Promise resolving to contract detail
 */
const fetchContract = async (id: string): Promise<ContractDetail> => {
  return api.get<ContractDetail>(`/contracts/${id}`);
};

/**
 * Create a new contract
 *
 * @param data - Contract creation data
 * @returns Promise resolving to created contract
 */
const createContract = async (data: CreateContract): Promise<ContractDetail> => {
  return api.post<ContractDetail>('/contracts', data);
};

/**
 * Update an existing contract
 *
 * @param id - Contract identifier
 * @param data - Contract update data
 * @returns Promise resolving to updated contract
 */
const updateContract = async (
  id: string,
  data: UpdateContract
): Promise<ContractDetail> => {
  return api.put<ContractDetail>(`/contracts/${id}`, data);
};

/**
 * Delete (archive) a contract
 *
 * @param id - Contract identifier
 * @returns Promise resolving when deletion is complete
 */
const deleteContract = async (id: string): Promise<void> => {
  return api.delete<void>(`/contracts/${id}`);
};

/**
 * Hook: Fetch paginated list of contracts
 *
 * Provides automatic caching, background refetching, and optimistic updates
 * for contract list data. Supports filtering, sorting, and pagination.
 *
 * @param params - Query parameters for filtering and pagination
 * @returns Query result with contracts data, loading state, and error state
 *
 * @example
 * ```typescript
 * const { data, isLoading, error } = useContracts({
 *   search: 'Microsoft',
 *   status: ContractStatus.Active,
 *   pageNumber: 1,
 *   pageSize: 25,
 * });
 * ```
 */
export const useContracts = (params?: ContractQueryParams) => {
  return useQuery({
    queryKey: queryKeys.contracts.list(params),
    queryFn: () => fetchContracts(params),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

/**
 * Hook: Fetch single contract by ID
 *
 * Retrieves detailed contract information with automatic caching.
 * The query is disabled if no ID is provided.
 *
 * @param id - Contract identifier
 * @returns Query result with contract detail, loading state, and error state
 *
 * @example
 * ```typescript
 * const { data: contract, isLoading } = useContract(contractId);
 * if (contract) {
 *   console.log(contract.title);
 * }
 * ```
 */
export const useContract = (id: string | undefined) => {
  return useQuery({
    queryKey: queryKeys.contracts.detail(id!),
    queryFn: () => fetchContract(id!),
    enabled: !!id,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

/**
 * Hook: Create new contract mutation
 *
 * Provides mutation function for creating contracts with automatic
 * cache invalidation and optimistic updates.
 *
 * @returns Mutation result with mutate function, loading state, and error state
 *
 * @example
 * ```typescript
 * const createMutation = useCreateContract();
 *
 * const handleSubmit = async (data: CreateContract) => {
 *   try {
 *     const newContract = await createMutation.mutateAsync(data);
 *     navigate(`/contracts/${newContract.id}`);
 *   } catch (error) {
 *     console.error('Failed to create contract:', error);
 *   }
 * };
 * ```
 */
export const useCreateContract = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createContract,
    onSuccess: () => {
      // Invalidate contract list queries to trigger refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.contracts.lists() });
      // Dashboard figures (summary, renewal forecast, cost per user) are computed from contracts
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });
};

/**
 * Hook: Update contract mutation
 *
 * Provides mutation function for updating contracts with automatic
 * cache invalidation and optimistic updates.
 *
 * @returns Mutation result with mutate function, loading state, and error state
 *
 * @example
 * ```typescript
 * const updateMutation = useUpdateContract();
 *
 * const handleUpdate = async (id: string, data: UpdateContract) => {
 *   try {
 *     await updateMutation.mutateAsync({ id, data });
 *     toast.success('Contract updated successfully');
 *   } catch (error) {
 *     toast.error('Failed to update contract');
 *   }
 * };
 * ```
 */
export const useUpdateContract = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateContract }) =>
      updateContract(id, data),
    onSuccess: (updatedContract) => {
      // Update the specific contract in cache
      queryClient.setQueryData(
        queryKeys.contracts.detail(updatedContract.id),
        updatedContract
      );

      // Invalidate contract list queries to trigger refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.contracts.lists() });
      // Dashboard figures (summary, renewal forecast, cost per user) are computed from contracts
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });
};

/**
 * Hook: Delete contract mutation
 *
 * Provides mutation function for deleting (archiving) contracts with
 * automatic cache invalidation.
 *
 * @returns Mutation result with mutate function, loading state, and error state
 *
 * @example
 * ```typescript
 * const deleteMutation = useDeleteContract();
 *
 * const handleDelete = async (id: string) => {
 *   if (confirm('Are you sure you want to delete this contract?')) {
 *     try {
 *       await deleteMutation.mutateAsync(id);
 *       navigate('/contracts');
 *     } catch (error) {
 *       toast.error('Failed to delete contract');
 *     }
 *   }
 * };
 * ```
 */
export const useDeleteContract = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteContract,
    onSuccess: (_, deletedId) => {
      // Remove the deleted contract from cache
      queryClient.removeQueries({ queryKey: queryKeys.contracts.detail(deletedId) });

      // Invalidate contract list queries to trigger refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.contracts.lists() });
      // Dashboard figures (summary, renewal forecast, cost per user) are computed from contracts
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });
};
